import { type CustomThemeConfig, type CustomThemeConfigOverride, isTokenWithUnit } from '../types';
import { hexToRgba } from './hex-to-rgba';

const applyPrimaryColorVariants = (
  theme: CustomThemeConfig,
  overrideTheme?: CustomThemeConfigOverride,
): CustomThemeConfig => {
  const { error, primary } = theme.color;

  primary.default6 = overrideTheme?.color?.primary?.default6 ?? hexToRgba({ hex: primary.default, opacity: 0.06 });
  primary.default80 = overrideTheme?.color?.primary?.default80 ?? hexToRgba({ hex: primary.default, opacity: 0.8 });
  primary.default10 = overrideTheme?.color?.primary?.default10 ?? hexToRgba({ hex: primary.default, opacity: 0.1 });
  error.default80 = overrideTheme?.color?.error?.default80 ?? hexToRgba({ hex: error.default, opacity: 0.8 });

  return theme;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const mergeRecords = <T extends object>({ base, override }: { base: T; override: object }): T => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Interfaces do not have an index signature
  const result: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Interfaces do not have an index signature
  const overrideRecord = override as Record<string, unknown>;

  for (const key of Object.keys(overrideRecord)) {
    const overrideValue: unknown = overrideRecord[key];
    if (overrideValue === undefined) {
      continue;
    }

    if (isTokenWithUnit(overrideValue)) {
      result[key] = { ...overrideValue };
      continue;
    }

    if (isPlainObject(overrideValue)) {
      const baseValue = result[key];
      const baseRecord = isPlainObject(baseValue) && !isTokenWithUnit(baseValue) ? baseValue : {};
      result[key] = mergeRecords({ base: baseRecord, override: overrideValue });
      continue;
    }

    result[key] = overrideValue;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- The structure of T is preserved by construction.
  return result as T;
};

export const mergeCustomThemeConfig = ({
  defaultTheme,
  overrideTheme,
}: {
  defaultTheme: CustomThemeConfig;
  overrideTheme?: CustomThemeConfigOverride;
}): CustomThemeConfig => {
  const merged =
    overrideTheme === undefined
      ? structuredClone(defaultTheme)
      : mergeRecords({
          base: structuredClone(defaultTheme),
          override: structuredClone(overrideTheme),
        });

  return applyPrimaryColorVariants(merged, overrideTheme);
};
